/**
 * Client HTTP typé minimaliste pour l'API PatrimonIA.
 *
 * Choix techniques :
 *  - fetch natif (pas de dépendance lourde type axios) — couvre 99% des besoins
 *  - typage stricte des entrées/sorties via les schemas générés
 *  - parse / lance une ApiClientError sur les status >= 400
 *  - URL base construite depuis VITE_API_BASE_URL + VITE_API_PREFIX
 *  - credentials: "include" sur toutes les requêtes → le cookie httpOnly access_token
 *    est transmis automatiquement par le navigateur
 */

import { config } from "@/lib/config";
import type { ApiError } from "./schema";

export class ApiClientError extends Error {
  status: number;
  detail: ApiError["detail"];

  constructor(status: number, detail: ApiError["detail"], message: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.detail = detail;
  }

  /** Message d'erreur convivial, en français.
   *
   * Une erreur serveur (5xx) ne montre jamais son détail technique
   * (« Internal server error ») ; une erreur de validation (422 Pydantic,
   * messages en anglais) devient une phrase française par champ.
   */
  toUserMessage(): string {
    if (this.status >= 500) return SERVER_ERROR_MESSAGE;
    if (typeof this.detail === "string") return this.detail;
    if (Array.isArray(this.detail) && this.detail.length > 0) {
      return [...new Set(this.detail.map(validationMessage))].join(" · ");
    }
    const detail: unknown = this.detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      const message = (detail as { message: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
    return this.message;
  }
}

export const SERVER_ERROR_MESSAGE =
  "Le serveur n'a pas pu enregistrer la modification — réessayez dans un instant.";

const FIELD_LABELS: Record<string, string> = {
  email: "E-mail",
  phone: "Téléphone",
  phone_country: "Indicatif",
  address: "Adresse",
  label: "Adresse",
  street: "Rue",
  postcode: "Code postal",
  city: "Ville",
  country: "Pays",
  first_name: "Prénom",
  last_name: "Nom",
  birth_date: "Date de naissance",
};

type ValidationItem = {
  msg: string;
  loc: (string | number)[];
  type: string;
  ctx?: Record<string, unknown>;
};

/** Traduit une erreur de validation Pydantic en phrase française. */
export function validationMessage(item: ValidationItem): string {
  const field = [...(item.loc ?? [])].reverse().find((p) => typeof p === "string" && p !== "body");
  const label = typeof field === "string" ? FIELD_LABELS[field] : undefined;
  const prefix = label ? `${label} : ` : "";
  const max = item.ctx?.max_length;
  const min = item.ctx?.min_length;
  if (item.type === "string_too_long" && typeof max === "number") {
    return `${prefix}${max} caractères maximum.`;
  }
  if (item.type === "string_too_short" && typeof min === "number") {
    return min <= 1 ? `${prefix}valeur obligatoire.` : `${prefix}${min} caractères minimum.`;
  }
  if (item.type === "missing") return `${prefix}valeur obligatoire.`;
  if (field === "email" || /e-?mail/i.test(item.msg)) {
    return "E-mail : adresse invalide (ex. nom@domaine.fr).";
  }
  if (item.type.startsWith("date") || item.type.startsWith("datetime")) {
    return `${prefix}date invalide.`;
  }
  if (item.type === "enum" || item.type === "literal_error") {
    return `${prefix}choix non reconnu.`;
  }
  return `${prefix}valeur invalide.`;
}

const BASE = `${config.apiBaseUrl.replace(/\/$/, "")}${config.apiPrefix}`;

/** Absolute URL of the API root (host + /api/v1). Use for raw `fetch` calls
 *  that can't go through `request()` — e.g. multipart/FormData uploads. */
export const API_BASE_URL = BASE;

type RequestOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Query string params */
  params?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Skip the 401→refresh retry to avoid infinite loops on the refresh endpoint itself. */
  _skipRefresh?: boolean;
};

let _onUnauthorized: (() => void) | null = null;
let _onImpersonationExpired: (() => void) | null = null;

/** Register a callback invoked when both the original request and the refresh fail with 401. */
export function setUnauthorizedHandler(handler: () => void): void {
  _onUnauthorized = handler;
}

export function setImpersonationExpiredHandler(handler: () => void): void {
  _onImpersonationExpired = handler;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path.startsWith("/") ? path : `/${path}`}`);

  if (options.params) {
    for (const [k, v] of Object.entries(options.params)) {
      if (v === null || v === undefined || v === "") continue;
      url.searchParams.append(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  let resp = await fetch(url.toString(), init);

  if (resp.status === 401 && resp.headers.get("X-Impersonation-Expired") === "true") {
    _onImpersonationExpired?.();
    throw new ApiClientError(401, "Session de support expirée", "Session de support expirée");
  }

  // Attempt a silent token refresh on 401, then replay the original request once.
  if (resp.status === 401 && !options._skipRefresh) {
    try {
      const refreshResp = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (refreshResp.ok) {
        // Replay the original request with the new cookie.
        resp = await fetch(url.toString(), init);
      } else {
        _onUnauthorized?.();
        throw new ApiClientError(401, "Session expirée", "Session expirée");
      }
    } catch (e) {
      if (e instanceof ApiClientError) throw e;
      _onUnauthorized?.();
      throw new ApiClientError(401, "Session expirée", "Session expirée");
    }
  }

  // 204 No Content
  if (resp.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    body = null;
  }

  if (!resp.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body
        ? (body as ApiError).detail
        : `HTTP ${resp.status}`;
    throw new ApiClientError(
      resp.status,
      detail,
      typeof detail === "string" ? detail : `HTTP ${resp.status}`,
    );
  }

  return body as T;
}

/**
 * GET binaire — utile pour télécharger un PDF, une image, etc. depuis
 * un endpoint authentifié, là où `api.get<T>` retournerait du JSON.
 *
 * Réutilise tout le pipeline `request()` (cookies, refresh 401, gestion
 * d'erreurs) sauf le `Accept: application/json` et le parse JSON final.
 * Lance une `ApiClientError` sur status >= 400 — comme les autres helpers.
 *
 * On ne passe pas par `request<T>` parce que celui-ci force `Accept: json`
 * et un parse JSON systématique. Dupliquer la logique de refresh ici
 * serait fragile ; à la place on factorise la partie commune dans une
 * micro-fonction de retry interne.
 */
export async function apiGetBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  return (await apiBinaryResponse("GET", path, signal)).blob();
}

/** Comme `apiGetBlob`, en conservant les en-têtes de réponse (ex.
 *  `X-Recueil-Incomplete-Sections`, exposé par la config CORS de l'API). */
export async function apiGetBlobWithHeaders(
  path: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; headers: Headers }> {
  const resp = await apiBinaryResponse("GET", path, signal);
  return { blob: await resp.blob(), headers: resp.headers };
}

export async function apiPostBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  return (await apiBinaryResponse("POST", path, signal)).blob();
}

/** POST d'un corps JSON qui renvoie un binaire (ex. aperçu PNG d'un document). */
export async function apiPostJsonBlob(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<Blob> {
  return (await apiBinaryResponse("POST", path, signal, body)).blob();
}

async function apiBinaryResponse(
  method: "GET" | "POST",
  path: string,
  signal?: AbortSignal,
  jsonBody?: unknown,
): Promise<Response> {
  const url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  const init: RequestInit = {
    method,
    credentials: "include",
    ...(signal !== undefined ? { signal } : {}),
    ...(jsonBody !== undefined
      ? { body: JSON.stringify(jsonBody), headers: { "Content-Type": "application/json" } }
      : {}),
  };

  let resp = await fetch(url, init);

  if (resp.status === 401 && resp.headers.get("X-Impersonation-Expired") === "true") {
    _onImpersonationExpired?.();
    throw new ApiClientError(401, "Session de support expirée", "Session de support expirée");
  }

  if (resp.status === 401) {
    const refreshResp = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (refreshResp.ok) {
      resp = await fetch(url, init);
    } else {
      _onUnauthorized?.();
      throw new ApiClientError(401, "Session expirée", "Session expirée");
    }
  }

  if (!resp.ok) {
    // Pour un endpoint binaire, le corps d'erreur reste en JSON côté backend.
    let detail: ApiError["detail"] = `HTTP ${resp.status}`;
    try {
      const body = (await resp.json()) as ApiError;
      if (body && "detail" in body) detail = body.detail;
    } catch {
      // garde le fallback
    }
    throw new ApiClientError(
      resp.status,
      detail,
      typeof detail === "string" ? detail : `HTTP ${resp.status}`,
    );
  }

  return resp;
}

export const api = {
  get: <T>(path: string, params?: RequestOptions["params"], signal?: AbortSignal) =>
    request<T>(path, {
      method: "GET",
      ...(params !== undefined ? { params } : {}),
      ...(signal !== undefined ? { signal } : {}),
    }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: "POST",
      body,
      ...(signal !== undefined ? { signal } : {}),
    }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: "PUT",
      body,
      ...(signal !== undefined ? { signal } : {}),
    }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: "PATCH",
      body,
      ...(signal !== undefined ? { signal } : {}),
    }),
  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, {
      method: "DELETE",
      ...(signal !== undefined ? { signal } : {}),
    }),
};

/** Requests for the platform console must never use the cabinet refresh flow. */
export const platformRequest = {
  get: <T>(path: string) => request<T>(path, { method: "GET", _skipRefresh: true }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body, _skipRefresh: true }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body, _skipRefresh: true }),
};
